// ErrorBoundary - 兜底 React component render 错误
// v1.9 P0: 加 reset 按钮 + 可折叠 stack trace + 报告问题按钮
// 任何 component 抛错都不会让整个 app 崩, 至少显示 fallback UI
import { Component, type ReactNode } from 'react';
import { Icon } from './Icon';
import { Button } from './ui/Button';

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: { componentStack?: string }) {
    console.error('[ErrorBoundary]', error, info);
  }

  reset = () => this.setState({ error: null });

  override render() {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }
      const err = this.state.error;
      const stack = err.stack?.split('\n').slice(0, 5).join('\n') ?? '';

      return (
        <div className="min-h-[60vh] flex items-center justify-center bg-bg p-4 sm:p-6">
          <div className="w-full max-w-md bg-surface border border-bad/30 rounded-lg p-5 sm:p-6 animate-fade-up">
            <div className="flex items-start gap-3 mb-3">
              <div className="shrink-0 w-10 h-10 rounded-full bg-bad/10 text-bad flex items-center justify-center">
                <Icon name="alert-circle" size="md" aria-hidden />
              </div>
              <div className="min-w-0">
                <h1 className="text-base font-semibold text-bad">页面出错了</h1>
                <p className="text-sm text-ink-2 mt-1 break-words">{err.message || '未知错误'}</p>
              </div>
            </div>

            <details className="text-xs text-ink-3 bg-surface-2 p-2 rounded mb-4 cursor-pointer">
              <summary className="select-none hover:text-ink-2 transition-colors">查看堆栈</summary>
              <pre className="mt-2 overflow-auto max-h-40 whitespace-pre-wrap leading-relaxed">
                {stack}
              </pre>
            </details>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button variant="primary" size="md" onClick={this.reset} iconLeft="refresh-cw" block>
                重试
              </Button>
              <Button
                variant="secondary"
                size="md"
                onClick={() => {
                  window.location.href = '/';
                }}
                iconLeft="home"
                block
              >
                回首页
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
